/**
 * INP² Auth Gate
 *
 * Renders the right surface based on backend-enforced auth state:
 *   loading        → spinner
 *   unauthenticated→ LoginScreen  (no setup screen — owner is bootstrapped via backend)
 *   authenticated  → AdminPanel (super_admin) or App (customer/demo)
 *
 * Super Admin lands directly in the admin panel; they have no tenant of their
 * own and the customer-facing dashboard isn't their primary surface.
 */

import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useAuth } from './AuthContext';
import LoginScreen from './LoginScreen';
import { migrateLegacyLocalStorage } from './migrateLegacyLocalStorage';

const AdminPanel = lazy(() => import('./AdminPanel'));
const INP2App = lazy(() => import('../App'));

function Spinner() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0f172a',
    }}>
      <div style={{
        width: 48,
        height: 48,
        border: '3px solid #334155',
        borderTop: '3px solid #06b6d4',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function AuthGate() {
  const { isLoading, isAuthenticated, isSuperAdmin, user, logout } = useAuth();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const migratedRef = useRef(false);

  useEffect(() => {
    if (user && user.companyId && !migratedRef.current) {
      migratedRef.current = true;
      migrateLegacyLocalStorage(user.id, user.companyId);
    }
  }, [user]);

  if (isLoading) return <Spinner />;
  if (!isAuthenticated) return <LoginScreen />;

  const showPanel = isSuperAdmin && !showAdminPanel;

  return (
    <Suspense fallback={<Spinner />}>
      <div style={{
        position: 'fixed',
        top: 12,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        gap: 8,
      }}>
        {isSuperAdmin && (
          <button
            onClick={() => setShowAdminPanel(v => !v)}
            style={{
              background: showAdminPanel ? '#06b6d4' : '#1e293b',
              color: showAdminPanel ? '#0f172a' : '#94a3b8',
              border: '1px solid #334155',
              borderRadius: 6,
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: '0.05em',
              fontFamily: 'monospace',
            }}
          >
            {showPanel ? 'Preview App →' : '← Admin Panel'}
          </button>
        )}
        <button
          onClick={logout}
          style={{
            background: '#1e293b',
            color: '#94a3b8',
            border: '1px solid #334155',
            borderRadius: 6,
            padding: '6px 14px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            letterSpacing: '0.05em',
            fontFamily: 'monospace',
          }}
        >
          Sign Out
        </button>
      </div>

      {showPanel
        ? <AdminPanel />
        : <INP2App companyId={user.companyId || 'demo-company-sandbox'} userRole={user.role} userName={user.name} />
      }
    </Suspense>
  );
}
